import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import {
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaMoon,
  FaObjectGroup,
  FaPlus,
  FaSearch,
  FaStickyNote,
  FaSun,
  FaTags,
  FaThLarge,
  FaThumbtack,
  FaTimes,
} from "react-icons/fa";

import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";
import NotesBoard from "../components/NotesBoard";
import NotesLibrary from "../components/NotesLibrary";
import CanvasStudio from "../components/canvas/CanvasStudio";
import useNotes from "../hooks/useNotes";
import { useAuth } from "../context/AuthContext";

const DARK_KEY = "sarvavya_notes_dark";
const VIEW_KEY = "sarvavya_notes_view";

const MIN_PANE = 18; // % — keep both panes usable while dragging the splitter
const MAX_PANE = 55;

function Notes() {
  const { isAdmin } = useAuth();
  const board = useNotes({ isAdmin });

  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "board");
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === "1");
  const [openPalette, setOpenPalette] = useState(null);
  const [paneWidth, setPaneWidth] = useState(26); // % of the split container
  const [libOpen, setLibOpen] = useState(true);

  const splitRef = useRef(null);
  const splitDrag = useRef(false);
  const studioRef = useRef(null);

  // Below md we drop both the free-drag corkboard and the side-by-side
  // split — neither is practical at phone width.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(DARK_KEY, dark ? "1" : "0");
  }, [dark]);
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  // ---- Splitter ----
  const onSplitMove = useCallback((e) => {
    if (!splitDrag.current || !splitRef.current) return;
    const rect = splitRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setPaneWidth(Math.min(MAX_PANE, Math.max(MIN_PANE, pct)));
  }, []);

  const onSplitEnd = useCallback(() => {
    splitDrag.current = false;
    document.body.style.userSelect = "";
    document.removeEventListener("pointermove", onSplitMove);
    document.removeEventListener("pointerup", onSplitEnd);
  }, [onSplitMove]);

  const startSplit = (e) => {
    e.preventDefault();
    splitDrag.current = true;
    // Stop the drag from selecting text across the whole page.
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onSplitMove);
    document.addEventListener("pointerup", onSplitEnd);
  };

  const pageBg = dark ? "#0b1120" : "#eef2f7";
  const chromeText = dark ? "#e2e8f0" : "#334155";
  const panelBg = dark ? "#111c30" : "#ffffff";
  const panelBorder = dark ? "1px solid #1f2f49" : "1px solid #e2e8f0";

  const studio = view === "studio";

  return (
    <>
      <CustomNavbar />

      <style>{`
        .nts-hero {
          position: relative; overflow: hidden; text-align: center;
          padding: clamp(44px, 6vw, 68px) 0;
          color: #0f172a;
          background:
            radial-gradient(38rem 22rem at 86% 4%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 48%, #fff8f5 100%);
        }
        .nts-hero__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(80px); opacity: .5;
          width: 22rem; height: 22rem; bottom: -8rem; left: -7rem;
          background: radial-gradient(circle, #fff3ec, transparent 70%);
        }
        .nts-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: #ffffffdd; color: #c2410c;
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .78rem; letter-spacing: .1em;
          box-shadow: 0 8px 20px rgba(194,65,12,.16);
        }
        .nts-hero__title {
          margin-top: 20px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.8rem, 4.4vw, 2.9rem); line-height: 1.14;
        }
        .nts-hero__title em {
          font-style: normal;
          background: linear-gradient(96deg, #c2410c, #ff6b35 60%, #b91c1c);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .nts-hero__lead {
          max-width: 640px; margin: 14px auto 0; color: #4a3a33;
          line-height: 1.8; font-size: clamp(.95rem, 1.6vw, 1.04rem);
        }
        .nts-stats {
          display: flex; flex-wrap: wrap; gap: 10px;
          justify-content: center; margin-top: 22px;
        }
        .nts-stat {
          display: inline-flex; align-items: center; gap: 8px;
          background: #ffffffcc; border: 1px solid #ffffff;
          padding: 8px 16px; border-radius: 999px;
          font-size: .84rem; font-weight: 600; color: #4a3a33;
          box-shadow: 0 6px 16px rgba(194,65,12,.12);
        }
        .nts-stat b { color: #c2410c; font-weight: 800; }
        .nts-stat svg { color: #ff6b35; }

        /* --- Toolbar --- */
        .nts-search { transition: box-shadow .2s ease; }
        .nts-search:focus-within { box-shadow: 0 0 0 4px rgba(255,144,102,.28) !important; }
        .nts-pill {
          border: none; border-radius: 999px; cursor: pointer;
          font-weight: 700; font-size: .85rem; padding: 8px 18px;
          display: inline-flex; align-items: center; gap: 8px;
          transition: background .2s ease, color .2s ease, transform .2s ease;
        }
        .nts-pill:hover { transform: translateY(-1px); }
        /* Navy on peach, not white: white on #ff6b35 measures 2.84:1 and
           fails AA even for large text, while navy reaches 6.30:1. */
        .nts-add {
          background: linear-gradient(135deg, #ff6b35, #ff9066);
          color: #0f172a; border: none; border-radius: 999px; cursor: pointer;
          font-weight: 700; padding: 10px 22px;
          display: inline-flex; align-items: center; gap: 9px;
          box-shadow: 0 8px 20px rgba(255,107,53,.42);
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .nts-add:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 28px rgba(255,107,53,.55);
        }
        .nts-tag {
          border: 1px solid transparent; border-radius: 999px;
          padding: 5px 14px; font-size: .8rem; cursor: pointer; font-weight: 600;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .nts-pill, .nts-add, .nts-tag, .nts-search { transition: none; }
          .nts-pill:hover, .nts-add:hover { transform: none; }
        }
      `}</style>

      {/* Hero */}
      <section className="nts-hero">
        <span className="nts-hero__blob" />
        <Container style={{ position: "relative", zIndex: 1 }}>
          <span className="nts-badge">
            <FaThumbtack />
            TEAM BOARD
          </span>

          <h1 className="nts-hero__title">
            {studio ? (
              <>
                Design <em>Studio</em>
              </>
            ) : (
              <>
                Sticky <em>Notes</em>
              </>
            )}
          </h1>

          <p className="nts-hero__lead">
            {studio
              ? isAdmin
                ? "Drag notes from the left onto the canvas, then add text, shapes and images — and export the result as a PNG."
                : "Viewing the team's designs. Layouts here are managed by an administrator."
              : isAdmin
                ? "Drag to arrange, pin what matters, tag, and manage the team board."
                : "Viewing the team board. Notes here are managed by an administrator."}
          </p>

          {/* Live counts — the page never showed how much was on the board */}
          {!board.loading && (
            <div className="nts-stats">
              <span className="nts-stat">
                <FaStickyNote />
                <b>{board.notes.length}</b>
                {board.notes.length === 1 ? "note" : "notes"}
              </span>
              <span className="nts-stat">
                <FaThumbtack />
                <b>{board.notes.filter((n) => n.pinned).length}</b> pinned
              </span>
              {board.allTags.length > 0 && (
                <span className="nts-stat">
                  <FaTags />
                  <b>{board.allTags.length}</b>
                  {board.allTags.length === 1 ? "tag" : "tags"}
                </span>
              )}
            </div>
          )}
        </Container>
      </section>

      {/* Generous bottom padding: the footer's CTA panel overlaps 84px
          upward into whatever section precedes it. */}
      <section
        style={{
          background: pageBg,
          padding: "36px 0 clamp(96px, 10vw, 132px)",
        }}
      >
        <Container fluid={studio} style={studio ? { maxWidth: "1600px" } : undefined}>
          {/* Toolbar */}
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            {/* View switcher */}
            <div
              className="d-flex"
              style={{
                background: panelBg,
                borderRadius: "30px",
                padding: "4px",
                boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
              }}
            >
              {[
                { key: "board", label: "Board", Icon: FaThLarge },
                { key: "studio", label: "Studio", Icon: FaObjectGroup },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  className="nts-pill"
                  aria-pressed={view === key}
                  style={{
                    background:
                      view === key
                        ? "linear-gradient(135deg,#ff6b35,#ff9066)"
                        : "transparent",
                    color: view === key ? "#0f172a" : chromeText,
                  }}
                >
                  <Icon />
                  {label}
                </button>
              ))}
            </div>

            <div
              className="d-flex align-items-center px-3 nts-search"
              style={{
                background: panelBg,
                borderRadius: "30px",
                flex: "1 1 240px",
                boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
              }}
            >
              <FaSearch style={{ color: "#94a3b8" }} />
              <input
                value={board.query}
                onChange={(e) => board.setQuery(e.target.value)}
                placeholder="Search notes, tags, people…"
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  padding: "10px 12px",
                  width: "100%",
                  color: chromeText,
                }}
              />
              {board.query && (
                <FaTimes
                  role="button"
                  onClick={() => board.setQuery("")}
                  style={{ color: "#94a3b8", cursor: "pointer" }}
                />
              )}
            </div>

            <button
              onClick={() => setDark((d) => !d)}
              title="Toggle dark mode"
              className="fw-bold"
              style={{
                border: "none",
                borderRadius: "30px",
                padding: "10px 16px",
                background: dark ? "#334155" : "#ffffff",
                color: dark ? "#fbbf24" : "#475569",
                boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
                cursor: "pointer",
              }}
            >
              {dark ? <FaSun /> : <FaMoon />}
            </button>

            {isAdmin ? (
              <button onClick={board.addNote} className="nts-add">
                <FaPlus />
                Add Note
              </button>
            ) : (
              <span
                className="fw-bold d-inline-flex align-items-center"
                style={{
                  background: dark ? "#334155" : "#e2e8f0",
                  color: dark ? "#e2e8f0" : "#475569",
                  padding: "10px 20px",
                  borderRadius: "30px",
                }}
              >
                <FaLock className="me-2" />
                View only
              </span>
            )}
          </div>

          {/* Tag filter chips */}
          {board.allTags.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {board.allTags.map((t) => (
                <button
                  key={t}
                  onClick={() => board.setTagFilter(board.tagFilter === t ? null : t)}
                  className="nts-tag"
                  aria-pressed={board.tagFilter === t}
                  style={{
                    background:
                      board.tagFilter === t
                        ? "linear-gradient(135deg,#ff6b35,#ff9066)"
                        : dark
                          ? "#334155"
                          : "#fff",
                    color:
                      board.tagFilter === t
                        ? "#0f172a"
                        : dark
                          ? "#e2e8f0"
                          : "#475569",
                    borderColor:
                      board.tagFilter === t
                        ? "transparent"
                        : dark
                          ? "#41506b"
                          : "#eadfd8",
                  }}
                >
                  #{t}
                </button>
              ))}
            </div>
          )}

          {board.error && (
            <div
              className="text-center mb-3"
              style={{
                color: "#b91c1c",
                background: "#fee2e2",
                padding: "10px",
                borderRadius: "10px",
              }}
            >
              {board.error}
            </div>
          )}

          {studio ? (
            <div
              ref={splitRef}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "16px" : 0,
                height: isMobile ? "auto" : "min(78vh, 820px)",
              }}
            >
              {/* Left: note library */}
              {(libOpen || isMobile) && (
                <div
                  style={{
                    width: isMobile ? "100%" : `${paneWidth}%`,
                    height: isMobile ? "340px" : "auto",
                    flexShrink: 0,
                    background: panelBg,
                    border: panelBorder,
                    borderRadius: "14px",
                    overflow: "hidden",
                  }}
                >
                  <NotesLibrary
                    board={board}
                    editable={isAdmin}
                    dark={dark}
                    onPlace={(id) => studioRef.current?.placeNote(id)}
                  />
                </div>
              )}

              {/* Splitter — drag to resize, click the chevron to collapse */}
              {!isMobile && (
                <div
                  onPointerDown={(e) => libOpen && startSplit(e)}
                  title={libOpen ? "Drag to resize" : ""}
                  style={{
                    width: libOpen ? "16px" : "26px",
                    flexShrink: 0,
                    cursor: libOpen ? "col-resize" : "default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    touchAction: "none",
                  }}
                >
                  <button
                    onClick={() => setLibOpen((o) => !o)}
                    onPointerDown={(e) => e.stopPropagation()}
                    title={libOpen ? "Hide notes" : "Show notes"}
                    style={{
                      width: "22px",
                      height: "44px",
                      borderRadius: "999px",
                      border: panelBorder,
                      background: panelBg,
                      color: dark ? "#94a3b8" : "#64748b",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
                      fontSize: "0.7rem",
                    }}
                  >
                    {libOpen ? <FaChevronLeft /> : <FaChevronRight />}
                  </button>
                </div>
              )}

              {/* Right: design canvas */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: isMobile ? "620px" : "auto",
                }}
              >
                <CanvasStudio
                  ref={studioRef}
                  notes={board.notes}
                  editable={isAdmin}
                  dark={dark}
                  // Double-clicking a note on the canvas edits the note
                  // itself, so the board and the library update with it.
                  onNoteInput={(id, text) => board.patchLocal(id, { text })}
                  onNoteSave={(id) => {
                    const n = board.notes.find((x) => x._id === id);
                    if (n) board.patchNote(id, { text: n.text });
                  }}
                />
              </div>
            </div>
          ) : (
            <NotesBoard
              board={board}
              editable={isAdmin}
              isMobile={isMobile}
              dark={dark}
              openPalette={openPalette}
              setOpenPalette={setOpenPalette}
            />
          )}
        </Container>
      </section>

      <Footer />
    </>
  );
}

export default Notes;
